import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { I18nProvider, useI18n } from './contexts/I18nContext';
import { UiVisibilityProvider, useUiVisibility } from './contexts/UiVisibilityContext';
import { Sidebar } from './components/navigation/Sidebar';
import { DashboardPage } from './pages/DashboardPage';
import { DevicesPage } from './pages/DevicesPage';
import { ClientsPage } from './pages/ClientsPage';
import { UsersPage } from './pages/UsersPage';
import { SignInPage } from './pages/SignInPage';
import { useClientAccess } from './hooks/useClientAccess';
import { NoClientAccessBanner } from './components/common/NoClientAccessBanner';
import { AccessDisabledScreen } from './components/common/AccessDisabledScreen';
import { DeviceTypesPage } from './pages/DeviceTypesPage';
import { OtaUpdatesPage } from './pages/OtaUpdatesPage';
import { ConfigTemplatesPage } from './pages/ConfigTemplatesPage';
import { AuditLogPage } from './pages/AuditLogPage';
import { SettingsPage } from './pages/SettingsPage';
import { WhoAmIPage } from './pages/WhoAmIPage';
import { ApiStatusIndicator } from './components/common/ApiStatusIndicator';
import { HeaderLanguageSelector } from './components/common/HeaderLanguageSelector';
import { DebugMenu } from './components/debug/DebugMenu';
import { applyThemeColors } from './utils/themeUtils';
import config from './config/env';
import './App.css';
import './styles/auth.css';
import './styles/devices.css';
import './styles/clients.css';
import './styles/modal.css';
import './styles/api-status.css';
import './styles/pages.css';
import './styles/dashboard.css';
import './styles/audit.css';
import './styles/dark-theme.css';
import './styles/enhanced-dark-theme.css';

// Main Layout component with sidebar
/**
 * Gate for ANEP-side screens. A customer's admin who types the URL directly
 * lands back on their dashboard rather than on a page of other tenants'
 * data. The API enforces the same boundary; this keeps the UI honest.
 */
const AnepOnlyRoute: React.FC<{ allowed: boolean; children: React.ReactElement }> = ({
  allowed,
  children,
}) => (allowed ? children : <Navigate to="/dashboard" replace />);


const MainLayout = () => {
  const { isAuthenticated, loading } = useAuth();
  const { t } = useI18n();
  const location = useLocation();
  const {
    shouldShowClientUI,
    canAccessCatalog,
    canAccessOtaUpdates,
    canViewAuditLog,
    isLockedOut,
  } = useClientAccess();

  // Show loading state while auth is being determined
  if (loading) {
    return (
      <div className="auth-loading-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  // Signed out: one screen, one action. No navigation to a place they
  // cannot go, no theme switch for an interface they cannot see.
  if (!isAuthenticated) {
    return <SignInPage />;
  }

  // Signed in but shut out: the same reasoning. A closed door does not need
  // a menu beside it.
  if (isLockedOut) {
    return <AccessDisabledScreen />;
  }

  return (
    <div className="main-layout">
      <Sidebar />
      <div className="main-content">
          <NoClientAccessBanner />
          <ErrorBoundary name={`route:${location.pathname}`} resetKey={location.pathname}>
            <Routes>
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/devices" element={<DevicesPage />} />
              {/* ANEP-side administration. Hiding the menu entry is not a
                  control — these routes are reachable by typing the URL, so
                  they are guarded here too and fall back to the dashboard. */}
              <Route
                path="/clients"
                element={<AnepOnlyRoute allowed={shouldShowClientUI}><ClientsPage /></AnepOnlyRoute>}
              />
              <Route path="/users" element={<UsersPage />} />
              <Route
                path="/device-types"
                element={<AnepOnlyRoute allowed={canAccessCatalog}><DeviceTypesPage /></AnepOnlyRoute>}
              />
              <Route
                path="/ota-updates"
                element={<AnepOnlyRoute allowed={canAccessOtaUpdates}><OtaUpdatesPage /></AnepOnlyRoute>}
              />
              <Route
                path="/config-templates"
                element={<AnepOnlyRoute allowed={canAccessCatalog}><ConfigTemplatesPage /></AnepOnlyRoute>}
              />
              <Route
                path="/audit-log"
                element={<AnepOnlyRoute allowed={canViewAuditLog}><AuditLogPage /></AnepOnlyRoute>}
              />
              <Route path="/settings" element={<SettingsPage />} />
              {/* /whoami — local-only diagnostic. Gated on config.dev.debug
                  (only the localhost + test env profiles enable it; the
                  production env profile sets debug=false), so the route
                  never appears in production builds even if someone types
                  the URL. Renders the current user's roles, useClientAccess
                  flags and raw JWT claims; also console.logs the same. */}
              {config.dev.debug && (
                <Route path="/whoami" element={<WhoAmIPage />} />
              )}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </ErrorBoundary>
      </div>
    </div>
  );
};

const AuthCallback = () => {
  const { loading } = useAuth();
  const { t } = useI18n();

  // Redirect to dashboard once authentication resolves (either outcome
  // lands on /, where AuthProvider surfaces the failure state).
  if (!loading) {
    return <Navigate to="/" replace />;
  }

  // Still processing
  return (
    <div className="auth-callback">
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>{t('auth.processingAuthentication')}</p>
      </div>
    </div>
  );
};

// Reject logoUrl values whose scheme could embed mixed-content warnings or
// non-image data URIs. Accepts http(s), root-relative paths, and data:image/*.
const sanitiseLogoUrl = (raw: string | undefined): string | null => {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('/')) return trimmed;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (/^data:image\/(png|jpe?g|gif|svg\+xml|webp);/i.test(trimmed)) return trimmed;
  return null;
};

const AppHeader: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();
  const { showApiStatus, showLanguageSelector, ready } = useUiVisibility();

  // The sign-in screen carries its own identity and its own language
  // switcher, so a second header above it would only repeat both.
  if (!isAuthenticated && !loading) {
    return null;
  }

  const safeLogoUrl = sanitiseLogoUrl(config.ui.logoUrl);
  return (
    <header className="App-header">
      <div className="header-content">
        <div className="header-title">
          {safeLogoUrl && (
            <img
              src={safeLogoUrl}
              alt={`${config.ui.companyName} Logo`}
              className="app-logo"
            />
          )}
          <div className="title-text">
            <h1>{config.ui.title}</h1>
            <p>{config.ui.companyName} Device Management System</p>
          </div>
        </div>
        <div className="header-auth">
          <div className="header-status">
            {/* Defer admin-hideable chrome until the visibility settings have
                resolved so nothing flashes visible on first paint only to
                disappear when the server response arrives. `ready` flips
                true immediately for unauthenticated users (no fetch) and
                after fetchEffectiveOnce resolves for authenticated ones. */}
            {ready && showApiStatus && <ApiStatusIndicator />}
            {/* DebugMenu self-gates: it honours `show_debug_menu` (global/role)
                AND the per-user `debug_menu_usernames` allowlist. Mounting it
                unconditionally (once ready) lets a non-empty allowlist render
                the menu for listed users even when `show_debug_menu === false`. */}
            {ready && <DebugMenu />}
            {ready && showLanguageSelector && <HeaderLanguageSelector />}
          </div>
        </div>
      </div>
    </header>
  );
};

function App() {
  useEffect(() => {
    applyThemeColors();
  }, []);

  return (
    <I18nProvider>
      <ThemeProvider>
        <AuthProvider>
          <UiVisibilityProvider>
            <Router>
              <ErrorBoundary name="app-root">
                <div className="App">
                  <AppHeader />

                  <main className="App-main">
                    <Routes>
                      <Route path="/auth/callback" element={<AuthCallback />} />
                      <Route
                        path="/*"
                        element={<MainLayout />}
                      />
                    </Routes>
                  </main>
                </div>
              </ErrorBoundary>
            </Router>
          </UiVisibilityProvider>
        </AuthProvider>
      </ThemeProvider>
    </I18nProvider>
  );
}

export default App;
